import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "api/axiosInterceptor";
import { AppDispatch } from "redux/store";
import {
  IAdminDashboardResponse,
  IAdminDashboardState,
} from "types/adminDashboard";
import { convertToCamelCase } from "utils/utils";
import { setIsLoading, setShowToast } from "./application";
import { apiUrl } from "api/apiUrl";

const initialState: IAdminDashboardState = {
  users: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageLength: 10,
      totalPages: 1,
      total: 1,
    },
  },
};

const slice = createSlice({
  name: "adminDashboard",
  initialState,
  reducers: {
    setUsers(state, action: PayloadAction<IAdminDashboardResponse>) {
      state.users.data = action.payload.data;
      state.users.pagination = action.payload.pagination;
    },
    removeUserFromState(state, action: PayloadAction<string>) {
      state.users.data = state.users.data.filter(
        (user) => user.login !== action.payload
      );
      if (state.users.pagination.total > 0) {
        state.users.pagination.total -= 1;
      }
    },
    addUserToState(
      state,
      action: PayloadAction<IAdminDashboardResponse["data"][0]>
    ) {
      state.users.data = [action.payload, ...state.users.data];
    },
  },
});

export const { setUsers, removeUserFromState, addUserToState } = slice.actions;

export default slice.reducer;

export const getAdminDashboardData = (pageNo: number, query: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `${apiUrl.companyUsers}records/`;
      const response = await api.post(path, {
        page_number: pageNo,
        query,
      });
      const formattedResponse = convertToCamelCase(response.data);
      dispatch(setUsers(formattedResponse));
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to fetch users!",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const removeUser =
  (userMail: string) => async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `${apiUrl.companyUsers}remove/`;
      const response = await api.delete(path, { data: { login: userMail } });

      if (response.status === 200 || response.status === 204) {
        dispatch(removeUserFromState(userMail));
      }
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to remove user!",
        })
      );
      throw error;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const addUser =
  (userData: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    [key: string]: unknown;
  }) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `${apiUrl.companyUsers}create/`;
      const response = await api.post(path, {
        login: userData.email,
        first_name: userData.firstName,
        last_name: userData.lastName,
        role: userData.role,
      });

      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to add user!",
        })
      );
      throw error;
    } finally {
      dispatch(setIsLoading(false));
    }
  };
